import type { ThreatModelContent } from '../../utils/api-client/api-schemas.ts'
import {
  coerceInternetFacing,
  INTERNET_FACING,
  type InternetFacing,
  isReachableFromUntrusted,
} from '../../utils/threat-model/exposure.ts'
import { buildCoverageModel, coerceRelevance, type ThreatRelevance } from '../../utils/threat-model/threat-coverage.ts'

/**
 * The estate's threat posture, aggregated from per-repository threat models.
 *
 * **There is no org-level endpoint.** Every threat-model route is scoped to one `repositoryId`, so a
 * caller fans out across the repository list and hands the results here. This module is the pure half:
 * given what came back, it computes the roll-up and the vulnerability recurrence, and it never fetches.
 *
 * **One definition of a gap, product-wide.** Coverage comes from {@link buildCoverageModel}, the same
 * function Threat coverage and Attack paths use, so "this vulnerability has no control" means exactly what
 * it means on a repository page. Re-deriving it here would let the org number drift from the per-repo
 * one, and the first person to notice would be a customer comparing two screens.
 *
 * **Absence is a category, never a zero.** A repository whose model has not been generated is counted
 * in `withoutModel` and excluded from every other denominator. Folding it in as "no gaps found" would
 * report a healthy estate for one that simply has not been looked at.
 */

export type RepositoryPosture = {
  repositoryId: string
  name: string
  /** Absent when this repository has no generated threat model. */
  content?: ThreatModelContent
}

export type GapRepository = {
  repositoryId: string
  name: string
}

export type VulnerabilityRecurrence = {
  /** Canonical name from the shared taxonomy, so spellings merge across repositories. */
  name: string
  /** How many modelled repositories carry this vulnerability at all. */
  repositoryCount: number
  /** Of those, how many have at least one control naming it. */
  defendedCount: number
  /** Of those, how many have none — the ones worth acting on. */
  gapCount: number
  /**
   * Which repositories those are, by name.
   *
   * The count alone is not actionable — it says a number of repositories are undefended against this
   * without saying which — and the aggregate is the only place that still knows.
   */
  gapRepositories: GapRepository[]
  /** Strongest relevance any repository reported, so a `present` anywhere is not hidden by `unlikely`. */
  strongestRelevance: ThreatRelevance
}

export type ExposureBand = {
  internetFacing: InternetFacing
  /** Modelled repositories the model puts in this band. Zero is reported, not omitted. */
  repositoryCount: number
}

export type RepositoryCoverage = {
  repositoryId: string
  name: string
  /** Classified once here, so the chip, the estate count and the mix cannot disagree about a value. */
  internetFacing: InternetFacing
  gapCount: number
  vulnerabilityCount: number
  /** Which vulnerabilities have no control here, by canonical name — the counterpart of `gapCount`. */
  gapVulnerabilities: string[]
  /** Gaps as a share of this repository's vulnerabilities, 0–1. What the ranking is ordered on. */
  undefendedShare: number
}

export type OrgThreatPosture = {
  estate: {
    /** Every repository the caller offered, modelled or not. */
    repositoryCount: number
    modelledCount: number
    /** Named rather than inferred: these have no model, so they are in no other figure here. */
    withoutModelCount: number
    /** Of the modelled repositories, how many the model calls reachable from an untrusted network. */
    internetFacingCount: number
    /** Total vulnerabilities with no control against them, across modelled repositories. */
    gapCount: number
    /** How many modelled repositories have at least one gap. */
    repositoriesWithGapsCount: number
    /**
     * Of `gapCount`, how many sit on a repository the model calls reachable from an untrusted network.
     *
     * The sharpest number the aggregate can produce, and the only one here that is an intersection of
     * two others rather than a slice of one — an undefended vulnerability behind a boundary and the same
     * vulnerability on a public surface are not the same problem, and nothing else on the page separates
     * them.
     */
    internetFacingGapCount: number
  }
  /**
   * The modelled repositories split by reachability, most exposed first.
   *
   * Always all four bands, including empty ones. A bar whose categories come and goes with the data
   * makes an empty band indistinguishable from a new one, and reads as a different chart per estate.
   *
   * This partitions `modelledCount` exactly — every repository has one value — which is what makes a
   * stacked bar an honest mark here. It is the same fact as `internetFacingCount`, unlumped: that figure
   * adds `yes` to `partial`, and `partial` is a hedge ("exposure depends on deployment") rather than a
   * second `yes`.
   */
  exposureMix: ExposureBand[]
  /** Vulnerabilities that recur across the estate, most widespread first. */
  vulnerabilities: VulnerabilityRecurrence[]
  /** Modelled repositories with the most uncovered vulnerabilities, worst first. */
  worstCovered: RepositoryCoverage[]
  /** Repositories with nothing to aggregate yet, so the page can offer to generate. */
  withoutModel: { repositoryId: string; name: string }[]
}

const RELEVANCE_STRENGTH: Record<ThreatRelevance, number> = {
  present: 3,
  likely: 2,
  unlikely: 1,
  not_applicable: 0,
}

const strongerOf = (a: ThreatRelevance, b: ThreatRelevance): ThreatRelevance =>
  RELEVANCE_STRENGTH[a] >= RELEVANCE_STRENGTH[b] ? a : b

export const aggregateOrgPosture = (repositories: readonly RepositoryPosture[]): OrgThreatPosture => {
  const modelled = repositories.filter(
    (entry): entry is RepositoryPosture & { content: ThreatModelContent } => !!entry.content
  )

  const withoutModel = repositories
    .filter(entry => !entry.content)
    .map(({ repositoryId, name }) => ({ repositoryId, name }))

  const perRepository = modelled.map(entry => {
    const coverage = buildCoverageModel({
      internetFacing: entry.content.exposure.internet_facing,
      controls: entry.content.security_controls.map(control => ({
        name: control.name,
        kind: control.kind,
        mitigates: control.mitigates,
        scope: control.scope,
      })),
      vulnerabilities: entry.content.vulnerability_class_impacts.map(impact => ({
        vulnClass: impact.vuln_class,
        relevance: coerceRelevance(impact.relevance),
        reachability: impact.reachability,
        blastRadius: impact.blast_radius,
      })),
    })

    return {
      repositoryId: entry.repositoryId,
      name: entry.name,
      internetFacing: coerceInternetFacing(entry.content.exposure.internet_facing),
      threats: coverage.threats,
    }
  })

  // Keyed on the canonical threat name, which is what makes a vulnerability recur rather than reappear
  // under a second spelling. `buildCoverageModel` has already merged within each repository.
  const byVulnerability = new Map<string, VulnerabilityRecurrence>()
  for (const repository of perRepository) {
    for (const threat of repository.threats) {
      const existing = byVulnerability.get(threat.name)
      const isDefended = threat.mitigatingControlIds.length > 0
      byVulnerability.set(threat.name, {
        name: threat.name,
        repositoryCount: (existing?.repositoryCount ?? 0) + 1,
        defendedCount: (existing?.defendedCount ?? 0) + (isDefended ? 1 : 0),
        gapCount: (existing?.gapCount ?? 0) + (threat.isGap ? 1 : 0),
        gapRepositories: threat.isGap
          ? [...(existing?.gapRepositories ?? []), { repositoryId: repository.repositoryId, name: repository.name }]
          : (existing?.gapRepositories ?? []),
        strongestRelevance: existing ? strongerOf(existing.strongestRelevance, threat.relevance) : threat.relevance,
      })
    }
  }

  const vulnerabilities = [...byVulnerability.values()].sort(
    (a, b) =>
      b.repositoryCount - a.repositoryCount ||
      b.gapCount - a.gapCount ||
      RELEVANCE_STRENGTH[b.strongestRelevance] - RELEVANCE_STRENGTH[a.strongestRelevance] ||
      a.name.localeCompare(b.name)
  )

  /**
   * Ranked by the *share* of a repository's vulnerabilities that no control names, not by the raw count.
   *
   * On raw counts a repository with twenty vulnerabilities and six gaps outranks one with four
   * vulnerabilities and four — when the second has no defenses at all and the first has fourteen. The
   * share answers "how much of this repository is undefended", which is what the heading claims.
   *
   * Absolute gap count breaks ties, so among repositories that are equally undefended the one with more
   * of them leads. A gap is a vulnerability, so the denominator is never zero when the numerator is not.
   */
  const worstCovered = perRepository
    .map(repository => {
      const gaps = repository.threats.filter(threat => threat.isGap)
      const gapCount = gaps.length
      const vulnerabilityCount = repository.threats.length
      return {
        repositoryId: repository.repositoryId,
        name: repository.name,
        internetFacing: repository.internetFacing,
        gapCount,
        vulnerabilityCount,
        // Sorted so the list under a count reads the same way on every visit.
        gapVulnerabilities: gaps.map(threat => threat.name).sort((a, b) => a.localeCompare(b)),
        undefendedShare: vulnerabilityCount > 0 ? gapCount / vulnerabilityCount : 0,
      }
    })
    .filter(repository => repository.gapCount > 0)
    .sort((a, b) => b.undefendedShare - a.undefendedShare || b.gapCount - a.gapCount || a.name.localeCompare(b.name))

  const exposureMix: ExposureBand[] = INTERNET_FACING.map(internetFacing => ({
    internetFacing,
    repositoryCount: perRepository.filter(repository => repository.internetFacing === internetFacing).length,
  }))

  return {
    estate: {
      repositoryCount: repositories.length,
      modelledCount: modelled.length,
      withoutModelCount: withoutModel.length,
      internetFacingCount: perRepository.filter(repository => isReachableFromUntrusted(repository.internetFacing))
        .length,
      gapCount: worstCovered.reduce((total, repository) => total + repository.gapCount, 0),
      repositoriesWithGapsCount: worstCovered.length,
      internetFacingGapCount: worstCovered
        .filter(repository => isReachableFromUntrusted(repository.internetFacing))
        .reduce((total, repository) => total + repository.gapCount, 0),
    },
    exposureMix,
    vulnerabilities,
    worstCovered,
    withoutModel,
  }
}
